<template>
  <div class="page-container">
    <header class="page-header">
      <div class="d-flex align-items-center">
        <portal-target name="sidebar-toggle" class="d-flex align-items-center mt-1"/>
        <h4 class="mb-0">{{ title }}</h4>
      </div>
      <div class="d-flex align-items-center">
        <b-button id="table-settings" class="custom-btn-secondary mr-1" v-b-toggle.sidebar-fields>
          <v-icon name="buttons/cog" :size="13"/>
        </b-button>
        <b-tooltip target="table-settings" triggers="hover" custom-class="custom-tooltip" noninteractive>
          Колонки
        </b-tooltip>
      </div>
    </header>

    <div class="page-body">
      <div class="page-filters">
        <filters-drawer
          :query.sync="filters.query"
          :is-busy="isBusy"
          :filter-tags="activeFilterTags"
          :disabled-submit="isStartDateEmpty"
          @remove-tag="removeFilterTag"
          @submit="onSearch"
          @clear="onClearFilters"
        >
          <b-col cols="12">
            <label class="small text-muted">Период</label>
            <b-row class="align-items-center">
              <b-col>
                <b-form-input
                  id="input_start_date"
                  size="sm"
                  type="date"
                  v-model.date="filters.start_date"
                  :state="isStartDateEmpty ? false : null"
                />
              </b-col>
              <b-col cols="auto" class="px-2">
                <span>&mdash;</span>
              </b-col>
              <b-col>
                <b-form-input
                  id="input_end_date"
                  size="sm"
                  type="date"
                  v-model.date="filters.end_date"
                />
              </b-col>
            </b-row>
          </b-col>
          <b-col v-if="isStartDateEmpty">
            <small class="text-danger">Укажите дату начала периода!</small>
          </b-col>
          <b-col v-if="canReadOther()" cols="12" class="mt-1">
            <label class="small text-muted">Сотрудник</label>
            <b-form-select
              id="input_executors"
              size="sm"
              v-model="filters.executor_id"
            >
              <option :value="0">Все сотрудники</option>
              <option v-for="option in activeExecutors" :value="option.id">
                {{ option.name }}
              </option>
            </b-form-select>
          </b-col>
        </filters-drawer>
      </div>

      <template v-if="isBusy || (items && items.length > 0)">
        <div class="page-table-container">
          <b-table
            id="register"
            ref="register"
            class="custom-table mb-0 text-small"
            sticky-header
            selectable
            select-mode="single"
            responsive
            :busy.sync="isBusy"
            :items="items"
            :fields="renderedFields"
            :current-page="currentPage"
            :per-page="perPage"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            :filter="filters.query"
            @filtered="onFiltered"
          >
            <template #table-busy>
              <tables-skeleton :fields="renderedFields" :rows="4"/>
            </template>

            <template #cell(task_id)="row">
              <b-link size="sm" variant="success" style="color: #1A9167 !important;" @click.stop="showTask(row)">
                {{row.item.task_id}}
              </b-link>
            </template>
            <template #cell(payment_type)="row">
              {{paymentTypeString(row.item.payment_type)}}
            </template>
            <!-- Приводим числовые значния с разделителем через "," -->
            <template #cell(total_hours)="row">
              {{ row.item.total_hours | formatNumber }}
            </template>
            <template #cell(total_worker_price)="row">
              {{ row.item.total_worker_price | formatNumber }}
            </template>
            <template #cell(total_price)="row">
              {{ row.item.total_price | formatNumber }}
            </template>
            <template #cell(total_sell_delta)="row">
              {{ row.item.total_sell_delta | formatNumber }}
            </template>
            <template #cell(total_delta)="row">
              {{ row.item.total_delta | formatNumber }}
            </template>
            <template #cell(total_manager)="row">
              {{ row.item.total_manager | formatNumber }}
            </template>
          </b-table>
        </div>

        <div class="page-pagination">
          <pagination-footer
            use-router
            :current-page.sync="currentPage"
            :total-rows="totalRows"
            :page-options="pageOptions"
            :per-page.sync="perPage"
            @page-click="onPagination"
            @per-page-change="onPerPageChange"
          >
            <template #footer-list>
              <div>
                Заявок: <b>{{total.tasks}}</b> |
                Часов: <b>{{total.total_hours | formatNumber}}</b> |
                Исполнителям: <b>{{total.total_worker_price | formatNumber}}</b> |
                Оплачено заказчиком: <b>{{total.total_price | formatNumber}}</b> |
                Дельта: <b>{{total.total_delta | formatNumber}}</b> |
                Сотруднику: <b>{{total.total_manager | formatNumber}}</b>
              </div>
            </template>
          </pagination-footer>
        </div>
      </template>

      <div v-else class="empty-state-container">
      <v-icon name="navbar/empty_table" :size="90"/>
      <h5>Нет записей в реестре.</h5>
      </div>
    </div>

    <DynamicTables
      storage-key="register_columns"
      sidebar-fields="sidebar-fields"
      :fields-model.sync="fields"
      fieldKey="task_id"
    />
  </div>
</template>

<script>
import reports from "../../network/reports";
import users from "../../network/users";
  import dateFormatter from '../../utilites/dateFormatter'
  import helpers from "../../utilites/helpers";
  import DynamicTables from "../../utilites/commonUi/DynamicTables.vue";
  import FiltersDrawer from "../../utilites/commonUi/FiltersDrawer.vue";
  import permissions, {PERM} from "../../utilites/permissions";
  import PaginationFooter from "../../utilites/commonUi/PaginationFooter.vue";
  import pageSearchService from "../../utilites/pageSearchService";
  import TablesSkeleton from "../../utilites/commonUi/TablesSkeleton.vue";

export default {
  name: 'Register',
  components: { TablesSkeleton, PaginationFooter, FiltersDrawer, DynamicTables },
  mixins: [
    reports, users,
    dateFormatter, helpers, permissions, pageSearchService
  ],
  data() {
    return {
      title: 'Общий реестр',
      items: [],
      fields: [
        {
          key: 'task_id',
          label: this.$t('message.register.task_id'),
          sortable: true,
          visible: true,
        },
        {
          key: 'start_date_format',
          label: this.$t('message.register.start_date_format'),
          sortable: true,
          class:'widthDateFormat',
          visible: true,
        },
        {
          key: 'executor_name',
          label: this.$t('message.register.executor_name'),
          sortable: true,
          visible: true,
        },
        {
          key: 'customer_name',
          label: this.$t('message.register.customer_name'),
          sortable: true,
          visible: true,
        },
        {
          key: 'start_address',
          label: this.$t('message.register.start_address'),
          sortable: true,
          class: 'widthMaterial',
          visible: true,
        },
        {
          key: 'total_hours',
          label: this.$t('message.register.total_hours'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_worker_price',
          label: this.$t('message.register.total_worker_price'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_price',
          label: this.$t('message.register.total_price'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_sell_delta',
          label: this.$t('message.register.total_sell_delta'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_delta',
          label: this.$t('message.register.total_delta'),
          sortable: true,
          visible: true,
        },
        {
          key: 'payment_type',
          label: this.$t('message.register.payment_type'),
          sortable: true,
          visible: true,
        },
        {
          key: 'total_manager',
          label: this.$t('message.register.total_manager'),
          sortable: true,
          visible: true,
        },
      ],
      executors: [],
      sortBy: 'start_date_format',
      sortDesc: false,
      isBusy: false,
      pageOptions: [50, 100, 500, 1000, 2000],
      filters: this.getDefaultFilters(),
      total: {
        tasks: 0,
        total_hours: 0,
        total_worker_price: 0,
        total_price: 0,
        total_delta: 0,
        total_manager: 0,
      },
    }
  },
  filters: {
    formatNumber(value) {
      Number(value).toLocaleString();
      return Number(value).toLocaleString("ru", {useGrouping: false});
    }
  },
  computed: {
    activeExecutors() {
      return this.executors.filter(item => item.active);
    },
    visibleFields() {
      return this.fields.filter(field => field.visible);
    },
    renderedFields() {
      const allFields = [
        ...this.visibleFields,
      ];
      return this.handleFixedCols(allFields);
    },
    activeFilterTags() {
      const tags = [];
      const defaults = this.getDefaultFilters();
      const start = this.filters.start_date;
      const end = this.filters.end_date;

      if (start || end) {
        let dateTag = '';

        if (start && end) {
          dateTag = `${this.formatTableDate(start)} - ${this.formatTableDate(end)}`;
        } else if (start) {
          dateTag = `с ${this.formatTableDate(start)}`;
        } else {
          dateTag = `по ${this.formatTableDate(end)}`;
        }
        tags.push({ key: 'date', label: dateTag });
      }

      if (this.filters.executor_id !== defaults.executor_id) {
        const foundItem = this.activeExecutors.find(s => s.id === this.filters.executor_id);
        tags.push({ key: 'executor', label: foundItem ? foundItem.name : 'Исполнитель'});
      }

      return tags;
    },
    isStartDateEmpty() {
      return !this.filters.start_date;
    },
  },
  async created() {
    this.isBusy = true;

    const result = await this.getUsers();

    if (result && result.data) {
      this.executors = result.data.users;
    }

    const query = this.$route.query;

    if (query) {
      if (query.query) {
        this.filters.query = query.query;
      }

      if (query.start_date) {
        this.filters.start_date = query.start_date;
      }

      if (query.end_date) {
        this.filters.end_date = query.end_date;
      }

      if (query.executor_id) {
        this.filters.executor_id = query.executor_id;
      }
    }

    await this.onSearch();
  },
  methods: {
    async getItems() {
      const data = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        executor_id: this.filters.executor_id,
      }

      this.isBusy = true;
      const result = await this.getRegisters(data);

      if (!result || !result.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        this.isBusy = false;
        return;
      }

      this.items = result.data;
      this.totalRows = result.data.length;

      await this.getTotals();
      this.isBusy = false;
    },
    async getTotals() {
      const data = {
        start_date: this.filters.start_date,
        end_date: this.filters.end_date,
        executor_id: this.filters.executor_id,
      }

      const result = await this.getRegisterTotals(data);

      if (!result || !result.data) {
        this.showError("Что-то пошло не так. Попробуйте обновить страницу и повторить действие.");
        return;
      }

      this.total = result.data;
    },
    showTask(row) {
      const path = `tasks/view/${row.item.task_id}`;
      const routeData = this.$router.resolve({ path: path});
      window.open(routeData.href, '_blank');
    },
    // Filters
    onFiltered (filteredItems) {
      this.totalRows = filteredItems.length;
      this.currentPage = 1
    },
    getDefaultFilters() {
      let filters = {
        query: '',
        start_date: null,
        end_date: null,
        executor_id: 0,
      }

      filters.start_date = this.getStartDateFilter();
      filters.end_date = this.getEndDateFilter();
      return filters;
    },
    onClearFilters() {
      const filters = this.getDefaultFilters();

      if (JSON.stringify(this.filters) === JSON.stringify(filters)) return;

      this.filters = filters;
      this.onSearch();
    },
    getStartDateFilter() {
      let date = new Date();
      date.setDate(date.getDate() - 15);
      return this.formatDate(date);
    },
    getEndDateFilter() {
      return this.formatDate(new Date());
    },
    removeFilterTag(tag) {
      if (tag.key === 'date') {
        this.filters.start_date = this.getStartDateFilter();
        this.filters.end_date = null;
      }
      else if (tag.key === 'executor') {
        this.filters.executor_id = 0;
      }
      this.onSearch();
    },
    canReadOther() {
      return this.hasPermission([PERM.REGISTER_READ_OTHER]);
    },
  }
}
</script>

<style>
#register-pagination {
  margin-bottom: 0;
}

.widthDateFormat{
  width: 100px;
}

.widthMaterial{
  width: 300px;
}
</style>



// WEBPACK FOOTER //
// src/components/Reports/Register.vue